import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, FileText } from "lucide-react";

/**
 * Generic PDF export checklist.
 * `sections`: [{ key, label, hint, defaultChecked }]
 * `onExport(selectedKeys)` is invoked with the chosen section keys.
 */
export default function ExportChecklistDialog({
  open,
  onOpenChange,
  title = "Export PDF",
  sections = [],
  onExport,
}) {
  const [selected, setSelected] = useState(
    () => new Set(sections.filter((s) => s.defaultChecked !== false).map((s) => s.key))
  );
  const [busy, setBusy] = useState(false);

  function toggle(key, checked) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  async function handleExport() {
    if (busy) return;
    setBusy(true);
    try {
      await onExport(Array.from(selected));
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-md" data-testid="export-checklist-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <FileText className="w-5 h-5" strokeWidth={1.5} />
            {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-stone-500 -mt-2">
          Choose what to include. Landscape A4.
        </p>
        <div className="space-y-3 pt-2 max-h-[55vh] overflow-y-auto pr-1">
          {sections.map((s) => (
            <label
              key={s.key}
              data-testid={`export-section-${s.key}`}
              className="flex items-start gap-3 p-3 rounded-xl border border-stone-200 hover:bg-stone-50 cursor-pointer"
            >
              <Checkbox
                checked={selected.has(s.key)}
                onCheckedChange={(v) => toggle(s.key, !!v)}
                className="mt-0.5"
                data-testid={`export-check-${s.key}`}
              />
              <div className="flex-1">
                <Label className="text-sm font-medium text-stone-900 cursor-pointer">
                  {s.label}
                </Label>
                {s.hint && (
                  <p className="text-xs text-stone-500 mt-0.5">{s.hint}</p>
                )}
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="rounded-full"
            data-testid="export-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={busy || selected.size === 0}
            className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
            data-testid="export-confirm-button"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />
            )}
            Export PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
